import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Seo from '@/components/base/Seo';
import { articles } from '@/mocks/articles';
import { mangas } from '@/mocks/mangas';

export default function Article() {
  const { id = '1' } = useParams();
  const article = articles.find((a) => a.id === Number(id)) || articles[0];
  const [formError, setFormError] = useState('');
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const relatedMangas = article.relatedMangaIds
    .map((mid) => mangas.find((m) => m.id === mid))
    .filter(Boolean);
  const moreArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('ok');
      form.reset();
      return;
    }
    const payload = new FormData();
    fd.forEach((v, k) => {
      if (k !== 'website_alt' && v) payload.append(k, v);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/da08qre859p3u981e140', {
        method: 'POST',
        body: payload,
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('ok');
        form.reset();
      } else {
        setFormError(String(msg || '提交失败，请稍后再试'));
        setStatus('err');
      }
    } catch {
      setFormError('网络异常，请稍后再试');
      setStatus('err');
    }
  }

  return (
    <>
      <Seo
        title={`${article.title} | 漫迹漫画追更资讯`}
        description={article.summary}
        path={`/article/${article.id}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            author: { '@type': 'Organization', name: article.author },
            datePublished: article.date,
            description: article.summary,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mangpush.github.io/' },
              { '@type': 'ListItem', position: 2, name: '追更资讯', item: 'https://mangpush.github.io/article/1' },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/article/1" className="hover:text-primary-600">追更资讯</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="line-clamp-1 font-medium text-foreground-800">{article.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-700">
              <i className="ri-price-tag-3-line" /> {article.tag}
            </span>
            <h1 className="mt-4 font-heading text-3xl leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-user-line" /> {article.author}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {article.date}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line" /> {article.readTime}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line" /> {(article.views / 1000).toFixed(1)}k 阅读</span>
            </div>

            <div className="mt-6 overflow-hidden rounded-xl">
              <img
                src={article.cover}
                alt={article.title}
                className="h-64 w-full object-cover object-top md:h-80"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {article.content.map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-foreground-800">{p}</p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4">
              <p className="text-xs font-medium text-foreground-500">本文标签</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">{article.category}</span>
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">{article.tag}</span>
              </div>
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            {/* 订阅表单 */}
            <div className="rounded-xl border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-lg text-foreground-950">订阅追更资讯</h3>
              <p className="mt-1 text-sm text-foreground-600">每周精选漫画更新提醒与最新话推送，直达你的邮箱。</p>
              {status === 'ok' ? (
                <p className="mt-4 rounded-lg bg-accent-100 px-4 py-3 text-sm font-medium text-accent-800">
                  订阅成功，感谢你的关注！
                </p>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="mt-4 flex flex-col gap-2.5">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="输入你的邮箱"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    订阅提醒
                  </button>
                  {status === 'err' && formError && (
                    <p className="text-xs text-red-600">{formError}</p>
                  )}
                </form>
              )}
            </div>

            {/* 相关漫画 */}
            {relatedMangas.length > 0 && (
              <div className="rounded-xl border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-lg text-foreground-950">相关漫画</h3>
                <div className="mt-4 flex flex-col gap-3">
                  {relatedMangas.map((m) => m && (
                    <Link
                      key={m.id}
                      to={`/category/${m.categoryId}`}
                      className="flex items-center gap-3 rounded-lg p-1 transition-colors hover:bg-background-200"
                    >
                      <img src={m.cover} alt={m.title} className="h-14 w-11 rounded-md object-cover object-top" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-foreground-950">{m.title}</p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">{m.latestChapter}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* 更多资讯 */}
        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="font-heading text-2xl text-foreground-950">更多追更资讯</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {moreArticles.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-40 w-full overflow-hidden">
                  <img src={a.cover} alt={a.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-sm font-bold leading-relaxed text-foreground-950 group-hover:text-primary-700">{a.title}</h3>
                  <p className="mt-2 text-xs text-foreground-500">{a.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}